import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Text, Button, SelectInput, TextInput, StatusBadge, Switch} from '@/ui-kits';
import {NA} from '@/constants';
import {DataTableColumn, SelectInputitem, PopularSpecialistItem} from '@/interface';
import {DataTable} from '@/components';
import {
  popularSpecialistDataSelector,
  popularSpecialistTotalPagesSelector,
  popularSpecialistTotalResultSelector,
  popularSpecialistCurrentPageSelector,
  popularityLimitSelector,
  patientAdminSuccessSelector,
  patientAdminFailureSelector,
  patientAdminStatus,
  allAppSpecializations,
} from '@/services/redux/selectors';
import {
  getPopularSpecialistListRequest,
  getPopularityLimitRequest,
  resetPatientAdminMessage,
  getSpecializationRequest,
  updatePopularityLimitRequest,
  overrideSpecialistPopularityRequest,
} from '@/services/redux/slices';
import {ManagePopularityLimitModal} from '@/components/patient-admin/ManagePopularityLimitModal';
import {ConfirmModal} from '@/components/common/confirmationModal';
import {ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes} from '@/utils/getMessages';
import {useToast} from '@/hooks';
import {StatusBadgeVariant} from '@/constants/enums';

export const PopularSpecialistManagement: React.FC = () => {
  // ==============================
  // hooks
  // ==============================
  const dispatch = useDispatch();
  const {showToast} = useToast();

  // ==============================
  // selectors
  // ==============================
  const specialistData = useSelector(popularSpecialistDataSelector);
  const specialistTotalPages = useSelector(popularSpecialistTotalPagesSelector);
  const specialistTotalResult = useSelector(popularSpecialistTotalResultSelector);
  const specialistCurrentPage = useSelector(popularSpecialistCurrentPageSelector);
  const specialistSuccess = useSelector(patientAdminSuccessSelector) as SuccessCodes;
  const specialistFailure = useSelector(patientAdminFailureSelector) as ErrorCodes;
  const isLoading = useSelector(patientAdminStatus);
  const categoryOptions = useSelector(allAppSpecializations);
  const popularityLimit = useSelector(popularityLimitSelector);

  // ==============================
  // states
  // ==============================
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<SelectInputitem | null>(null);
  const [selectedAutoPopularFilter, setSelectedAutoPopularFilter] = useState<SelectInputitem | null>(null);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [page, setPage] = useState(1);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isManageLimitModalOpen, setIsManageLimitModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedSpecialist, setSelectedSpecialist] = useState<PopularSpecialistItem | null>(null);

  // Auto Popular filter options
  const autoPopularOptions: SelectInputitem[] = [
    {id: 1, label: 'Yes'},
    {id: 0, label: 'No'},
  ];

  // ==============================
  // functions
  // ==============================
  function resetFilter() {
    setSearchTerm('');
    setSelectedCategoryFilter(null);
    setSelectedAutoPopularFilter(null);
    setIsFilterActive(false);
    setPage(1);
  }

  const fetchSpecialists = useCallback(() => {
    let isFilterApplied = false;
    setErrorMessage('');
    const payload: {page: number; search_text?: string; category?: number; auto_popular?: boolean} = {
      page,
    };

    if (searchTerm.trim()) {
      payload.search_text = searchTerm.trim();
      isFilterApplied = true;
    }

    if (selectedCategoryFilter?.id) {
      payload.category = selectedCategoryFilter.id;
      isFilterApplied = true;
    }

    if (selectedAutoPopularFilter?.id !== undefined && selectedAutoPopularFilter?.id !== null) {
      payload.auto_popular = selectedAutoPopularFilter.id === 1;
      isFilterApplied = true;
    }

    if (isFilterApplied) {
      setIsFilterActive(true);
    } else {
      setIsFilterActive(false);
    }
    dispatch(getPopularSpecialistListRequest(payload));
  }, [searchTerm, selectedCategoryFilter, selectedAutoPopularFilter, page, dispatch]);

  // ==============================
  // data
  // ==============================

  const columns: DataTableColumn<PopularSpecialistItem>[] = useMemo((): DataTableColumn<PopularSpecialistItem>[] => {
    return [
      {
        name: 'specialist_name',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            SPECIALIST
          </Text>
        ),
        render: row => (
          <Text variant="body" className="!font-FigtreeSemiBold truncate text-sm text-ui_input_border_dark">
            {row.name ? `Dr. ${row.name}` : NA}
          </Text>
        ),
        align: 'left',
        width: {minWidth: '200px', maxWidth: '250px'},
      },
      {
        name: 'category',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            CATEGORY
          </Text>
        ),
        render: row => (
          <Text variant="body" className="!font-FigtreeSemiBold text-sm text-ui_input_border_dark">
            {row.specialization?.label ?? NA}
          </Text>
        ),
        align: 'left',
        width: {minWidth: '150px', maxWidth: '320px'},
      },
      {
        name: 'score',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            SCORE
          </Text>
        ),
        render: row => (
          <Text variant="body" className="!font-FigtreeSemiBold text-sm text-ui_input_border_dark">
            {row.popularity_score ?? NA}
          </Text>
        ),
        align: 'center',
        width: {minWidth: '100px', maxWidth: '120px'},
      },
      {
        name: 'auto_popular',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            AUTO POPULAR
          </Text>
        ),
        render: row => (
          <Text variant="body" className="!font-FigtreeSemiBold text-sm text-ui_input_border_dark">
            {row.auto_popular ? 'Yes' : 'No'}
          </Text>
        ),
        align: 'center',
        width: {minWidth: '120px', maxWidth: '170px'},
      },
      {
        name: 'admin_override',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            ADMIN OVERRIDE
          </Text>
        ),
        render: row => (
          <div className="flex justify-center">
            <div className="scale-125">
              <Switch
                value={row.admin_overridden}
                onChange={() => {
                  setSelectedSpecialist(row);
                  setIsConfirmModalOpen(true);
                }}
                disabled={(row.final_popularity && row.auto_popular) || !popularityLimit || popularityLimit === 0}
              />
            </div>
          </div>
        ),
        align: 'center',
        width: {minWidth: '150px', maxWidth: '190px'},
      },
      {
        name: 'final_status',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            FINAL STATUS
          </Text>
        ),
        render: row => (
          <div className="flex justify-center">
            <StatusBadge status={row.final_popularity ? StatusBadgeVariant.YELLOW : StatusBadgeVariant.BLUE}>
              {row.final_popularity ? 'Popular' : 'Not Popular'}
            </StatusBadge>
          </div>
        ),
        align: 'center',
        width: {minWidth: '150px', maxWidth: '180px'},
      },
    ];
  }, [popularityLimit]);

  // ==============================
  // side effects
  // ==============================
  useEffect(() => {
    dispatch(getSpecializationRequest());
    dispatch(getPopularityLimitRequest());
  }, [dispatch]);

  useEffect(() => {
    const timeout = setTimeout(fetchSpecialists, 500);
    return () => clearTimeout(timeout);
  }, [fetchSpecialists]);

  const handleSavePopularityLimit = useCallback((data: {limit: number}) => {
    dispatch(updatePopularityLimitRequest(data));
  }, [dispatch]);

  const handleConfirmOverride = useCallback(() => {
    if (selectedSpecialist) {
      dispatch(overrideSpecialistPopularityRequest({id: selectedSpecialist.id}));
      setIsConfirmModalOpen(false);
      setSelectedSpecialist(null);
    }
  }, [selectedSpecialist, dispatch]);

  useEffect(() => {
    if (specialistSuccess) {
      if (specialistSuccess === 'S-10032') {
        // Specialist list fetched successfully - no need to show toast
      } else if (String(specialistSuccess) === 'S-10077') {
        // Popularity limit updated successfully
        showToast(getSuccessMessage(specialistSuccess as SuccessCodes), 'success');
        setIsManageLimitModalOpen(false);
        // Keep the current limit value instead of resetting to 0
        // Refetch the specialist list to show updated data
        fetchSpecialists();
      } else if (String(specialistSuccess) === 'S-10078') {
        // Specialist popularity overridden successfully
        showToast(getSuccessMessage(specialistSuccess as SuccessCodes), 'success');
        // Refetch the specialist list to show updated data
        fetchSpecialists();
      }
    }
    if (specialistFailure) {
      if (['E-10044', 'E-10054'].includes(specialistFailure)) {
        setErrorMessage(getErrorMessage(specialistFailure));
      } else {
        showToast(getErrorMessage(specialistFailure), 'failure');
      }
    }
    return () => {
      dispatch(resetPatientAdminMessage());
    };
  }, [specialistSuccess, specialistFailure, dispatch, showToast, fetchSpecialists]);

  return (
    <div className="flex flex-col gap-4 p-2 sm:p-4 md:p-6 lg:p-8">
      <div className="flex flex-col md:flex-row justify-between items-start gap-4">
        <div>
          <Text variant="heading" className="mb-1 text-ui_blue text-sm whitespace-normal min-[375px]:whitespace-nowrap sm:text-lg md:whitespace-normal min-[960px]:whitespace-nowrap md:text-2xl lg:text-3xl">
            Popular Specialist Management
          </Text>
          <Text variant="title" className="text-ui_input_border_dark !font-FigtreeRegular text-sm sm:text-base md:text-lg lg:text-xl">
            Control which specialists receive the Popular tag and their display priority.
          </Text>
        </div>
        <Button title="Manage Limits" variant="secondary" leftIcon="settings" onClick={() => setIsManageLimitModalOpen(true)} className="w-auto md:w-auto" />
      </div>

      <div className="flex flex-col md:grid md:grid-cols-3 gap-4 items-end">
        <TextInput
          value={searchTerm}
          onChangeText={setSearchTerm}
          placeholder="Search by specialist name"
          wrapperClassName="border"
          className="w-full"
          leftIcon="search_blue"
          iconClassName="ml-3"
        />
        <div className="grid grid-cols-2 md:contents gap-4 w-full">
          <SelectInput
            value={selectedCategoryFilter?.id || ''}
            onChange={setSelectedCategoryFilter}
            placeholder="Category Name"
            options={categoryOptions}
            className="w-full"
            wrapperClassName="border"
          />
          <div className="md:flex md:gap-4 md:items-end">
            <SelectInput
              value={selectedAutoPopularFilter?.id !== undefined ? selectedAutoPopularFilter.id : ''}
              onChange={setSelectedAutoPopularFilter}
              placeholder="Auto Popular"
              options={autoPopularOptions}
              className="w-full"
              wrapperClassName="border"
            />
            {isFilterActive && (
              <Button title="Reset Filters" variant="secondary" onClick={resetFilter} className="mb-0 hidden md:block" />
            )}
          </div>
        </div>
        {isFilterActive && (
          <Button title="Reset Filters" variant="secondary" onClick={resetFilter} className="w-auto md:hidden self-start" />
        )}
      </div>

      <DataTable
        columns={columns}
        data={specialistData}
        totalPages={specialistTotalPages}
        currentPage={specialistCurrentPage}
        totalResult={specialistTotalResult}
        onPageChange={setPage}
        errorMessage={errorMessage}
        loading={isLoading}
        pageSize={50}
      />

      {isManageLimitModalOpen && (
        <ManagePopularityLimitModal
          open={isManageLimitModalOpen}
          onClose={() => setIsManageLimitModalOpen(false)}
          onSave={handleSavePopularityLimit}
          currentLimit={popularityLimit ?? undefined}
        />
      )}

      <ConfirmModal
        open={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedSpecialist(null);
        }}
        onConfirm={handleConfirmOverride}
        header="Do you want to change popular status?"
        message="This will update the specialist's final popular visibility across the platform."
        cancelButtonText="Cancel"
        confirmButtonText="Confirm"
        primaryCta="confirm"
      />
    </div>
  );
};

